/**
 * Icons.
 *
 * Phosphor paths, inlined. The design brief requires self-hosted assets only (this instance
 * runs on a tailnet with no internet route), and an icon font or an npm icon package would be
 * a runtime dependency for ten glyphs. These are the glyphs the navigation uses; anything else
 * in the UI is a shape drawn in place.
 */
import type { SVGProps } from 'react';

type Props = SVGProps<SVGSVGElement> & { size?: number };

function Icon({ size = 15, children, ...rest }: Props) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 256 256"
      fill="currentColor"
      style={{ display: 'block' }}
      aria-hidden
      {...rest}
    >
      {children}
    </svg>
  );
}

/** Chart-line glyph for gateable quality targets. */
export function BudgetsIcon(props: Props) {
  return (
    <Icon {...props}>
      <path d="M216,200H32V48a8,8,0,0,0-16,0V208a8,8,0,0,0,8,8H216a8,8,0,0,0,0-16ZM176,160a8,8,0,0,1-5.66-2.34l-44.69-44.7L97.66,141a8,8,0,0,1-11.32,0L32,86.63,43.31,75.31,92,124l28-28a8,8,0,0,1,11.31,0L181.66,146.34,224,104V152a8,8,0,0,1-8,8Z" />
    </Icon>
  );
}

export function BoardIcon(props: Props) {
  return (
    <Icon {...props}>
      <path d="M230.91,172A8,8,0,0,1,228,182.91l-96,56a8,8,0,0,1-8.06,0l-96-56A8,8,0,0,1,36,169.09l92,53.65,92-53.65A8,8,0,0,1,230.91,172ZM220,121.09l-92,53.65L36,121.09A8,8,0,0,0,28,134.91l96,56a8,8,0,0,0,8.06,0l96-56A8,8,0,1,0,220,121.09ZM24,80a8,8,0,0,1,4-6.91l96-56a8,8,0,0,1,8.06,0l96,56a8,8,0,0,1,0,13.82l-96,56a8,8,0,0,1-8.06,0l-96-56A8,8,0,0,1,24,80Zm23.88,0L128,126.74,208.12,80,128,33.26Z" />
    </Icon>
  );
}

export function OpsIcon(props: Props) {
  return (
    <Icon {...props}>
      <path d="M224,120h-8V88a16,16,0,0,0-16-16H136V56h8a16,16,0,0,0,16-16V24A16,16,0,0,0,144,8H112A16,16,0,0,0,96,24V40a16,16,0,0,0,16,16h8V72H56A16,16,0,0,0,40,88v32H32a16,16,0,0,0-16,16v16a16,16,0,0,0,16,16h8v32a16,16,0,0,0,16,16H200a16,16,0,0,0,16-16V168h8a16,16,0,0,0,16-16V136A16,16,0,0,0,224,120ZM112,24h32V40H112ZM56,88H200v32H56Zm144,112H56V168H200Zm24-48H32V136H224Z" />
    </Icon>
  );
}

export function RunsIcon(props: Props) {
  return (
    <Icon {...props}>
      <path d="M197.58,129.06,146,110l-19-51.62a15.92,15.92,0,0,0-29.88,0L78,110l-51.62,19a15.92,15.92,0,0,0,0,29.88L78,178l19,51.62a15.92,15.92,0,0,0,29.88,0L146,178l51.62-19a15.92,15.92,0,0,0,0-29.88ZM137,164.22a8,8,0,0,0-4.74,4.74L112,223.85,91.78,169A8,8,0,0,0,87,164.22L32.15,144,87,123.78A8,8,0,0,0,91.78,119L112,64.15,132.22,119a8,8,0,0,0,4.74,4.74L191.85,144Z" />
    </Icon>
  );
}

export function SettingsIcon(props: Props) {
  return (
    <Icon {...props}>
      <path d="M128,80a48,48,0,1,0,48,48A48.05,48.05,0,0,0,128,80Zm0,80a32,32,0,1,1,32-32A32,32,0,0,1,128,160Zm88-29.84q.06-2.16,0-4.32l14.92-18.64a8,8,0,0,0,1.48-7.06,107.21,107.21,0,0,0-10.88-26.25,8,8,0,0,0-6-3.93l-23.72-2.64q-1.48-1.56-3-3L186,40.54a8,8,0,0,0-3.94-6,107.71,107.71,0,0,0-26.25-10.87,8,8,0,0,0-7.06,1.49L130.16,40Q128,40,125.84,40L107.2,25.11a8,8,0,0,0-7.06-1.48A107.6,107.6,0,0,0,73.89,34.51a8,8,0,0,0-3.93,6L67.32,64.27q-1.56,1.49-3,3L40.54,70a8,8,0,0,0-6,3.94,107.71,107.71,0,0,0-10.87,26.25,8,8,0,0,0,1.49,7.06L40,125.84Q40,128,40,130.16L25.11,148.8a8,8,0,0,0-1.48,7.06,107.21,107.21,0,0,0,10.88,26.25,8,8,0,0,0,6,3.93l23.72,2.64q1.49,1.56,3,3L70,215.46a8,8,0,0,0,3.94,6,107.71,107.71,0,0,0,26.25,10.87,8,8,0,0,0,7.06-1.49L125.84,216q2.16.06,4.32,0l18.64,14.92a8,8,0,0,0,7.06,1.48,107.21,107.21,0,0,0,26.25-10.88,8,8,0,0,0,3.93-6l2.64-23.72q1.56-1.48,3-3L215.46,186a8,8,0,0,0,6-3.94,107.71,107.71,0,0,0,10.87-26.25,8,8,0,0,0-1.49-7.06Zm-16.1-6.5a73.93,73.93,0,0,1,0,8.68,8,8,0,0,0,1.74,5.48l14.19,17.73a91.57,91.57,0,0,1-6.23,15L187,173.11a8,8,0,0,0-5.1,2.64,74.11,74.11,0,0,1-6.14,6.14,8,8,0,0,0-2.64,5.1l-2.51,22.58a91.32,91.32,0,0,1-15,6.23l-17.74-14.19a8,8,0,0,0-5-1.75h-.48a73.93,73.93,0,0,1-8.68,0,8,8,0,0,0-5.48,1.74L100.45,215.8a91.57,91.57,0,0,1-15-6.23L82.89,187a8,8,0,0,0-2.64-5.1,74.11,74.11,0,0,1-6.14-6.14,8,8,0,0,0-5.1-2.64L46.43,170.6a91.32,91.32,0,0,1-6.23-15l14.19-17.74a8,8,0,0,0,1.74-5.48,73.93,73.93,0,0,1,0-8.68,8,8,0,0,0-1.74-5.48L40.2,100.45a91.57,91.57,0,0,1,6.23-15L69,82.89a8,8,0,0,0,5.1-2.64,74.11,74.11,0,0,1,6.14-6.14A8,8,0,0,0,82.89,69L85.4,46.43a91.32,91.32,0,0,1,15-6.23l17.74,14.19a8,8,0,0,0,5.48,1.74,73.93,73.93,0,0,1,8.68,0,8,8,0,0,0,5.48-1.74L155.55,40.2a91.57,91.57,0,0,1,15,6.23L173.11,69a8,8,0,0,0,2.64,5.1,74.11,74.11,0,0,1,6.14,6.14,8,8,0,0,0,5.1,2.64l22.58,2.51a91.32,91.32,0,0,1,6.23,15l-14.19,17.74A8,8,0,0,0,199.87,123.66Z" />
    </Icon>
  );
}

/** Phosphor ListChecks: a list of items, each signed off. */
export function ReviewQueueIcon(props: Props) {
  return (
    <Icon {...props}>
      <path d="M224,128a8,8,0,0,1-8,8H128a8,8,0,0,1,0-16h88A8,8,0,0,1,224,128ZM128,72h88a8,8,0,0,0,0-16H128a8,8,0,0,0,0,16Zm88,112H128a8,8,0,0,0,0,16h88a8,8,0,0,0,0-16ZM82.34,42.34,56,68.69,45.66,58.34A8,8,0,0,0,34.34,69.66l16,16a8,8,0,0,0,11.32,0l32-32A8,8,0,0,0,82.34,42.34Zm0,64L56,132.69,45.66,122.34a8,8,0,0,0-11.32,11.32l16,16a8,8,0,0,0,11.32,0l32-32a8,8,0,0,0-11.32-11.32Zm0,64L56,196.69,45.66,186.34a8,8,0,0,0-11.32,11.32l16,16a8,8,0,0,0,11.32,0l32-32a8,8,0,0,0-11.32-11.32Z" />
    </Icon>
  );
}

export function GraphIcon(props: Props) {
  return (
    <Icon {...props}>
      <path d="M208,176a32,32,0,0,0-28.7,17.85L88.7,141.07a32.29,32.29,0,0,0,0-26.14L179.3,62.15A32,32,0,1,0,171.24,48L80.65,100.81a32,32,0,1,0,0,54.38L171.24,208A32,32,0,1,0,208,176Zm0-144a16,16,0,1,1-16,16A16,16,0,0,1,208,32ZM64,144a16,16,0,1,1,16-16A16,16,0,0,1,64,144Zm144,80a16,16,0,1,1,16-16A16,16,0,0,1,208,224Z" />
    </Icon>
  );
}

export function AgentsIcon(props: Props) {
  return (
    <Icon {...props}>
      <path d="M200,48H136V16a8,8,0,0,0-16,0V48H56A32,32,0,0,0,24,80V192a32,32,0,0,0,32,32H200a32,32,0,0,0,32-32V80A32,32,0,0,0,200,48Zm16,144a16,16,0,0,1-16,16H56a16,16,0,0,1-16-16V80A16,16,0,0,1,56,64H200a16,16,0,0,1,16,16ZM116,120a12,12,0,1,1-12-12A12,12,0,0,1,116,120Zm48,0a12,12,0,1,1-12-12A12,12,0,0,1,164,120Zm-4.87,48a44.06,44.06,0,0,1-62.26,0,8,8,0,0,1,11.3-11.32,28,28,0,0,0,39.66,0,8,8,0,1,1,11.3,11.32Z" />
    </Icon>
  );
}

export function PlusIcon(props: Props) {
  return (
    <Icon {...props}>
      <path d="M224,128a8,8,0,0,1-8,8H136v80a8,8,0,0,1-16,0V136H40a8,8,0,0,1,0-16h80V40a8,8,0,0,1,16,0v80h80A8,8,0,0,1,224,128Z" />
    </Icon>
  );
}

export function HeartbeatIcon(props: Props) {
  return (
    <Icon {...props}>
      <path d="M240,128a8,8,0,0,1-8,8H204.94l-37.78,75.58A8,8,0,0,1,160,216h-.4a8,8,0,0,1-7.08-5.14L95.35,60.76,63.11,129.52A8,8,0,0,1,56,136H24a8,8,0,0,1,0-16H50.85L88.69,50.47A8,8,0,0,1,103.5,54l57.13,151.1,31.61-63.23A8,8,0,0,1,200,136h32A8,8,0,0,1,240,128Z" />
    </Icon>
  );
}

export function UsersThreeIcon(props: Props) {
  return (
    <Icon {...props}>
      <path d="M244.8,150.4a8,8,0,0,1-11.2-1.6A51.6,51.6,0,0,0,192,128a8,8,0,0,1,0-16,24,24,0,1,0-23.24-32,8,8,0,1,1-15.5-4A40,40,0,1,1,219,117.51a67.94,67.94,0,0,1,27.43,21.68A8,8,0,0,1,244.8,150.4ZM141.73,154a40,40,0,1,0-59.46,0,72.08,72.08,0,0,0-27.65,16.47A8,8,0,1,0,65.32,181,56,56,0,0,1,112,160a56,56,0,0,1,46.68,21,8,8,0,0,0,11.74-10.86A72.08,72.08,0,0,0,141.73,154ZM72,120a24,24,0,1,1,24,24A24,24,0,0,1,72,120Zm142.45,72.65a8,8,0,0,0-11.18,1.23,56,56,0,0,1-83.87,1.12,8,8,0,1,0-11.92,10.67,72.12,72.12,0,0,0,107.84-1.45A8,8,0,0,0,214.45,192.65Z" />
    </Icon>
  );
}

export function CubeIcon(props: Props) {
  return (
    <Icon {...props}>
      <path d="M223.68,66.15,135.68,18a15.88,15.88,0,0,0-15.36,0l-88,48.17a16,16,0,0,0-8.32,14v95.64a16,16,0,0,0,8.32,14l88,48.17a15.88,15.88,0,0,0,15.36,0l88-48.17a16,16,0,0,0,8.32-14V80.18A16,16,0,0,0,223.68,66.15ZM128,32l80.34,44L128,120,47.66,76ZM40,90l80,43.78v85.76L40,175.82Zm96,129.57V133.82L216,90v85.78Z" />
    </Icon>
  );
}

export function RocketIcon(props: Props) {
  return (
    <Icon {...props}>
      <path d="M223.85,47.12a16,16,0,0,0-15-15c-66,4.29-121.7,54.05-127.83,117.42a60.47,60.47,0,0,0-7.31,12.36l-29,48.42A15.94,15.94,0,0,0,47.16,232l48.43-29a60.47,60.47,0,0,0,12.36-7.31C169.79,189.85,219.56,134.28,223.85,47.12ZM91.43,185.44l-36,21.63,21.63-36a44.18,44.18,0,0,1,7.37-9.15l16.18,16.18A44.18,44.18,0,0,1,91.43,185.44ZM136,152a24,24,0,1,1,24-24A24,24,0,0,1,136,152Z" />
    </Icon>
  );
}

export function HourglassIcon(props: Props) {
  return (
    <Icon {...props}>
      <path d="M200,75.64V40a16,16,0,0,0-16-16H72A16,16,0,0,0,56,40V76a16.07,16.07,0,0,0,6.4,12.8L114.67,128,62.4,167.2A16.07,16.07,0,0,0,56,180v36a16,16,0,0,0,16,16H184a16,16,0,0,0,16-16V180.36a16.59,16.59,0,0,0-6.23-12.81L141.33,128l52.46-40.55A16.59,16.59,0,0,0,200,75.64ZM184,180v36H72V180l56-43.08Zm0-104.41L128,119.08,72,76V40H184Z" />
    </Icon>
  );
}

export function RefreshIcon(props: Props) {
  return (
    <Icon {...props}>
      <path d="M240,56v48a8,8,0,0,1-8,8H184a8,8,0,0,1,0-16H211.4L184.81,71.64l-.25-.24a80,80,0,1,0-1.67,114.78,8,8,0,0,1,11,11.63A95.44,95.44,0,0,1,128,224h-1.32A96,96,0,1,1,195.75,60L224,85.8V56a8,8,0,1,1,16,0Z" />
    </Icon>
  );
}

export function BellIcon(props: Props) {
  return (
    <Icon {...props}>
      <path d="M221.8,175.94C216.25,166.38,208,139.33,208,104a80,80,0,1,0-160,0c0,35.34-8.26,62.38-13.81,71.94A16,16,0,0,0,48,200H88.81a40,40,0,0,0,78.38,0H208a16,16,0,0,0,13.8-24.06ZM128,216a24,24,0,0,1-22.62-16h45.24A24,24,0,0,1,128,216ZM48,184c7.7-13.24,16-43.92,16-80a64,64,0,1,1,128,0c0,36.05,8.28,66.73,16,80Z" />
    </Icon>
  );
}

export function MuteIcon(props: Props) {
  return (
    <Icon {...props}>
      <path d="M53.92,34.62A8,8,0,1,0,42.08,45.38L61.32,66.55A79.62,79.62,0,0,0,48,104c0,35.33-8.26,62.38-13.81,71.94A16,16,0,0,0,48,200H88.81a40,40,0,0,0,78.38,0h20.65l14.24,15.62a8,8,0,1,0,11.84-10.76ZM128,216a24,24,0,0,1-22.62-16h45.24A24,24,0,0,1,128,216ZM48,184c7.7-13.24,16-43.92,16-80a63.71,63.71,0,0,1,8.16-31.23L188.9,184Zm155.9-8.06a8,8,0,0,1-11.31-11.31C196.2,161,208,138.87,208,104A80,80,0,0,0,90.72,33.35a8,8,0,0,1-7.42-14.17A96,96,0,0,1,224,104C224,141.29,211.55,168.29,203.9,175.94Z" />
    </Icon>
  );
}

export function SpeakerIcon(props: Props) {
  return (
    <Icon {...props}>
      <path d="M155.51,24.81a8,8,0,0,0-8.42.88L77.25,80H32A16,16,0,0,0,16,96v64a16,16,0,0,0,16,16H77.25l69.84,54.31A8,8,0,0,0,160,224V32A8,8,0,0,0,155.51,24.81ZM32,96H72v64H32ZM144,207.64,88,164.07V91.93l56-43.57Zm54-106.08a40,40,0,0,1,0,52.88,8,8,0,0,1-12-10.58,24,24,0,0,0,0-31.72,8,8,0,0,1,12-10.58ZM248,128a87.69,87.69,0,0,1-25.23,62.67,8,8,0,0,1-11.32-11.32,72,72,0,0,0,0-102.7,8,8,0,1,1,11.34-11.3A87.76,87.76,0,0,1,248,128Z" />
    </Icon>
  );
}

/** Palette glyph for the colour-theme picker. */
export function PaletteIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden>
      <path d="M12 3a9 9 0 1 0 0 18c1.1 0 1.8-.8 1.8-1.7 0-.5-.2-.9-.5-1.2-.3-.3-.5-.7-.5-1.2 0-.9.8-1.7 1.7-1.7H16a5 5 0 0 0 5-5c0-4-4-7.2-9-7.2Z" />
      <circle cx="7.5" cy="11" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="10.5" cy="7.2" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="15" cy="7.4" r="1.1" fill="currentColor" stroke="none" />
    </svg>
  );
}

/** "Aa" glyph for the font picker. */
export function TypeIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
      <path d="M3 19 8.5 5h1L15 19M5.2 14h7.6" />
      <circle cx="18.2" cy="16.2" r="2.8" />
      <path d="M21 13.4V19" />
    </svg>
  );
}

export function HelpIcon({ size = 16 }: { size?: number }) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden><circle cx="12" cy="12" r="9" /><path d="M9 9a3 3 0 0 1 6 0c0 2-3 2-3 4M12 16v1" /></svg>;
}
